import { useEffect, useState } from "react";
import { api, fmtMoney, parseApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Printer, Ban, RotateCcw, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTimeFixed, statusVi, statusTone } from "@/lib/pos-utils";

export function OrderDetailDialog({ orderId, onClose, onChanged }) {
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    api.get(`/pos/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch((e) => {
        toast.error(parseApiError(e, "Lỗi tải đơn").message);
        onClose();
      });
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const action = async (path, label, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.post(`/pos/orders/${orderId}/${path}`, {});
      toast.success(`${label} thành công`);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(parseApiError(e, `Lỗi ${label.toLowerCase()}`).message);
    } finally {
      setBusy(false);
    }
  };

  if (!orderId) return null;
  const status = order?.status;
  const canVoid = status === "paid";
  const canRefund = status === "paid";
  const canCancel = status === "open";

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="pos-order-detail">
        <DialogHeader>
          <DialogTitle>Chi tiết đơn {order?.code || ""}</DialogTitle>
          <DialogDescription>Xem dòng hàng, thanh toán và thực hiện hủy/hoàn nếu cần.</DialogDescription>
        </DialogHeader>

        {!order ? (
          <div className="py-10 text-center text-stone-700"><Loader2 className="w-5 h-5 mx-auto animate-spin" aria-hidden="true" /></div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Info label="Trạng thái">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${statusTone(status)}`}>{statusVi(status)}</span>
              </Info>
              <Info label="Thời gian">{fmtDateTimeFixed(order.created_at)}</Info>
              <Info label="Kênh">{order.channel}</Info>
              <Info label="Tổng">{fmtMoney(order.total)}</Info>
              {order.table_no && <Info label="Bàn">{order.table_no}</Info>}
              {order.customer_id && <Info label="Khách hàng">{order.customer_name || order.customer_id}</Info>}
            </div>

            <div>
              <div className="text-xs font-bold text-stone-700 mb-1">Dòng hàng</div>
              <table className="w-full text-xs">
                <thead className="bg-stone-50"><tr>
                  {["SKU", "Tên", "SL", "Đơn giá", "Thành tiền"].map((h) => <th key={h} className="text-left px-2 py-1 text-stone-700">{h}</th>)}
                </tr></thead>
                <tbody>
                  {(order.lines || []).map((l) => (
                    <tr key={l.id} className="border-t border-stone-100">
                      <td className="px-2 py-1 font-mono">{l.sku_code}</td>
                      <td className="px-2 py-1">{l.sku_name}</td>
                      <td className="px-2 py-1 font-mono text-right">{l.qty}</td>
                      <td className="px-2 py-1 font-mono text-right">{fmtMoney(l.unit_price)}</td>
                      <td className="px-2 py-1 font-mono text-right font-bold">{fmtMoney(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {order.payments && order.payments.length > 0 && (
              <div>
                <div className="text-xs font-bold text-stone-700 mb-1">Thanh toán</div>
                <ul className="text-xs space-y-1">
                  {order.payments.map((p) => (
                    <li key={p.id} className="flex justify-between font-mono">
                      <span>{p.method}</span><span>{fmtMoney(p.amount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => window.print()} className="rounded-sm">
            <Printer className="w-4 h-4 mr-1" aria-hidden="true" />In
          </Button>
          {canCancel && (
            <Button type="button" variant="outline" onClick={() => action("cancel", "Hủy đơn", "Hủy đơn này?")} disabled={busy} className="rounded-sm text-amber-800" data-testid="btn-order-cancel">
              <XCircle className="w-4 h-4 mr-1" aria-hidden="true" />Hủy
            </Button>
          )}
          {canVoid && (
            <Button type="button" variant="outline" onClick={() => action("void", "Void đơn", "Void đơn đã thanh toán này?")} disabled={busy} className="rounded-sm text-red-700" data-testid="btn-order-void">
              <Ban className="w-4 h-4 mr-1" aria-hidden="true" />Void
            </Button>
          )}
          {canRefund && (
            <Button type="button" variant="outline" onClick={() => action("refund", "Hoàn tiền", "Hoàn toàn bộ đơn này?")} disabled={busy} className="rounded-sm text-blue-700" data-testid="btn-order-refund">
              <RotateCcw className="w-4 h-4 mr-1" aria-hidden="true" />Hoàn
            </Button>
          )}
          <Button type="button" onClick={onClose} className="rounded-sm bg-stone-800 hover:bg-stone-900 ml-auto">Đóng</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[10px] text-stone-700 uppercase tracking-wider">{label}</div>
      <div className="text-sm font-semibold">{children}</div>
    </div>
  );
}
